/**
 * MobileOrderFullBody — the scrollable body of the full-order screen, matching
 * Trioty Orders Mobile: "Order" + id, a chips row, a vertical Timeline (from real
 * statusHistory), Items + summary, Payment (method · rows · receipt), and
 * Customer & delivery. Display-only — all actions live in the screen's action bar.
 */

import type { OrderStatus, SellerOrderDetail } from "@/lib/api/types";
import { useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { formatEgp } from "@/modules/orders/lib/order-money";
import { relativeAgo } from "@/modules/orders/lib/order-status-display";
import { resolveItemTitle } from "@/modules/orders/lib/resolve-item-title";
import { getOrderItemImageUrl } from "@/lib/order-image";
import { ImageThumb } from "@/shared/components/PlaceholderImage";
import { fulVisual, StatusRing } from "@/modules/orders/components/order-row/order-visuals";
import { useGovernoratesQuery } from "@/modules/settings/services/useGovernoratesQuery";
import { useOrderProofUrlsById } from "@/modules/orders/services/useOrderProofQuery";
import { STATUS_GROUP_LABEL, statusGroup } from "./orders-mobile-model";

type Props = {
  order: SellerOrderDetail;
  locale: string;
  isDark: boolean;
  storeSlug: string;
};

const SECTION = "text-[12px] font-bold uppercase tracking-[0.05em] text-[color:var(--text-muted)]";
const CARD = "rounded-[15px] bg-[color:var(--soft)]";

function paymentKindOf(o: SellerOrderDetail): "cod" | "paid" | "deposit" {
  if (o.paymentMethod === "COD") return "cod";
  if (o.paidAmount > 0 && o.paidAmount < o.requiredAmount) return "deposit";
  return "paid";
}

export function MobileOrderFullBody({ order, locale, isDark, storeSlug }: Props) {
  const t = useTranslations();
  const { data: governorates } = useGovernoratesQuery({ storeSlug });
  const gov = governorates?.find((g) => g.id === order.governorateId);
  const toast = useToast();
  const proofUrlsById = useOrderProofUrlsById({
    storeSlug,
    orderId: order.id,
    enabled: order.paymentProofs.length > 0,
  });

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(order.buyerAddress);
      toast.success(t(keys.orders.copyAddressAction));
    } catch {
      // Clipboard unavailable (permissions / non-secure context) — no-op.
    }
  };

  // Structured address fields (like the desktop drawer), when the granular
  // address is present. Empties are filtered out.
  const addressRows = order.address
    ? [
        { label: t(keys.orders.addressAreaLabel), value: order.address.area },
        { label: t(keys.orders.addressStreetLabel), value: order.address.street },
        { label: t(keys.orders.addressBuildingLabel), value: order.address.building },
        { label: t(keys.orders.addressFloorLabel), value: order.address.floor ?? "" },
        { label: t(keys.orders.addressApartmentLabel), value: order.address.apartment ?? "" },
        { label: t(keys.orders.addressLandmarkLabel), value: order.address.landmark ?? "" },
      ].filter((r) => r.value)
    : [];
  const govName = gov ? (locale === "ar" ? gov.nameAr : gov.nameEn ?? gov.nameAr) : null;

  const kind = paymentKindOf(order);
  const insetBg = "color-mix(in srgb, var(--text-primary) 6%, transparent)";

  // Why an order was closed — shown as a banner for cancelled/declined orders.
  const reasonNote =
    order.status === "cancelled"
      ? order.cancelReasonNote
      : order.status === "declined"
        ? order.declineReasonNote
        : null;

  // ── chips ── (tinted by type, matching the design)
  const stageRing = fulVisual(order.status, isDark);
  const statusTint = {
    bg: `color-mix(in srgb, ${stageRing.color} 13%, transparent)`,
    fg: stageRing.color,
  };
  const payTint = {
    cod: { bg: "rgba(245,177,51,.16)", fg: "#D9910F" },
    paid: { bg: "rgba(74,222,128,.16)", fg: "#16A34A" },
    deposit: { bg: "rgba(142,180,255,.16)", fg: "#2E6BD6" },
  }[kind];
  const payLabel =
    kind === "cod"
      ? t(keys.orders.paymentMethodCod)
      : kind === "paid"
        ? t(keys.orders.payPaidOnline)
        : t(keys.orders.payDeposit);

  // ── timeline (real statusHistory, deduped by coarse stage) ──
  const timeline = buildTimeline(order, locale, t);

  // ── items + summary ──
  const lines = order.items;
  const subtotal = lines.reduce((s, it) => s + it.unitPrice * it.quantity, 0);
  const summary: { label: string; value: string }[] = [
    { label: t(keys.orders.subtotalLabel), value: formatEgp(subtotal) },
    {
      label: t(keys.orders.shippingLabel),
      value: order.shippingFee > 0 ? formatEgp(order.shippingFee) : t(keys.orders.freeShipping),
    },
  ];
  if (order.couponDiscountAmount > 0)
    summary.push({
      label: order.couponCode
        ? `${t(keys.orders.discountLabel)} · ${order.couponCode}`
        : t(keys.orders.discountLabel),
      value: `– ${formatEgp(order.couponDiscountAmount)}`,
    });

  // ── payment rows ──
  const payRows =
    kind === "deposit"
      ? [
          { label: t(keys.orders.depositPaidLabel), amount: formatEgp(order.paidAmount), pos: true },
          {
            label: t(keys.orders.balanceDueLabel),
            amount: formatEgp(order.requiredAmount - order.paidAmount),
            pos: false,
          },
        ]
      : kind === "paid"
        ? [{ label: t(keys.orders.paidInFull), amount: formatEgp(order.paidAmount), pos: true }]
        : [
            {
              label: t(keys.orders.collectOnDelivery),
              amount: formatEgp(order.requiredAmount || order.totalAmount),
              pos: false,
            },
          ];
  const methodLabel =
    order.paymentMethod === "COD"
      ? t(keys.orders.paymentMethodCod)
      : t(keys.orders.paymentMethodExternalOnline);

  return (
    <div className="px-5 pb-4 pt-2">
      <div className={SECTION}>{t(keys.orders.orderLabel)}</div>
      <div className="mt-[3px] text-[30px] font-extrabold tracking-[-0.025em] text-[color:var(--text-primary)]">
        #{order.orderCode}
      </div>

      {/* chips */}
      <div className="mt-4 flex flex-wrap gap-2">
        <Chip tint={statusTint}>
          <StatusRing color={stageRing.color} kind={stageRing.ring} frac={stageRing.frac} isDark={isDark} />
          {t(STATUS_GROUP_LABEL[statusGroup(order.status)])}
        </Chip>
        <Chip tint={payTint}>
          <span className="h-2 w-2 rounded-full" style={{ background: payTint.fg }} />
          {payLabel}
        </Chip>
        <Chip>
          <Avatar name={order.customer.name} />
          {order.customer.name || "—"}
        </Chip>
        <Chip>
          <BoxIcon />
          {t(keys.orders.itemsCountSummary, { count: order.itemCount })}
        </Chip>
      </div>

      {/* cancel / decline reason */}
      {reasonNote ? (
        <div
          className="mt-4 flex items-start gap-2.5 rounded-[12px] px-3.5 py-3"
          style={{ background: "color-mix(in srgb, var(--error-500) 10%, transparent)" }}
        >
          <span className="mt-px shrink-0 text-[color:var(--error-500)]">
            <AlertIcon />
          </span>
          <div className="min-w-0">
            <div className="text-[11.5px] font-bold text-[color:var(--error-500)]">
              {t(
                order.status === "cancelled"
                  ? keys.orders.actionCancelTitle
                  : keys.orders.actionDeclineTitle,
              )}
            </div>
            <div dir="auto" className="mt-0.5 text-[12.5px] text-[color:var(--text-primary)]">
              {reasonNote}
            </div>
          </div>
        </div>
      ) : null}

      {/* timeline */}
      <div className="mt-6">
        <div className={`${SECTION} mb-4`}>{t(keys.orders.timelineTitle)}</div>
        {timeline.map((s, i) => (
          <div key={i} className="flex gap-[13px]">
            <div className="flex flex-col items-center">
              <TimelineNode state={s.state} />
              {i < timeline.length - 1 ? (
                <span
                  className="my-1 w-0.5 flex-1"
                  style={{ minHeight: 22, background: "var(--line2)" }}
                />
              ) : null}
            </div>
            <div className="flex-1 pb-4">
              <div
                className="text-[14px] font-bold tracking-[-0.01em]"
                style={{ color: s.state === "current" ? "var(--text-primary)" : "var(--text-primary)" }}
              >
                {s.label}
              </div>
              {s.sub ? (
                <div className="mt-0.5 text-[12px] text-[color:var(--text-muted)]">{s.sub}</div>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {/* items */}
      <div className="mt-3">
        <div className={`${SECTION} mb-3`}>{t(keys.orders.itemsTitle)}</div>
        <div className={`${CARD} overflow-hidden`}>
          {lines.map((it) => (
            <div
              key={`${it.productId}-${it.productVariantId}`}
              className="flex items-center gap-3 border-b border-[color:var(--hair)] px-[14px] py-[11px]"
            >
              <ImageThumb
                src={getOrderItemImageUrl(it)}
                seed={it.productId}
                alt={resolveItemTitle(it, locale)}
                size="h-[38px] w-[38px]"
                bg="bg-white"
                className="rounded-[10px]"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[14px] font-semibold text-[color:var(--text-primary)]">
                  {resolveItemTitle(it, locale)}
                </div>
                {(() => {
                  const variantLine = it.selectedOptions
                    .map((opt) =>
                      locale === "ar" ? opt.value.ar : (opt.value.en ?? opt.value.ar),
                    )
                    .filter(Boolean)
                    .join(" · ");
                  return variantLine ? (
                    <div className="truncate text-[11.5px] text-[color:var(--text-muted)]">
                      {variantLine}
                    </div>
                  ) : null;
                })()}
              </div>
              <span className="shrink-0 text-[12px] font-semibold text-[color:var(--text-muted)]">
                ×{it.quantity}
              </span>
              <span className="shrink-0 text-[13.5px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
                {formatEgp(it.unitPrice * it.quantity)}
              </span>
            </div>
          ))}
          <div className="px-[15px] py-[13px]">
            {summary.map((sm) => (
              <div key={sm.label} className="flex items-center justify-between py-1">
                <span className="text-[12.5px] font-semibold text-[color:var(--text-muted)]">
                  {sm.label}
                </span>
                <span className="text-[13px] font-bold text-[color:var(--text-secondary)]">
                  {sm.value}
                </span>
              </div>
            ))}
            <div className="mt-1.5 flex items-center justify-between border-t border-[color:var(--card-br)] pt-2.5">
              <span className="text-[14px] font-bold text-[color:var(--text-primary)]">
                {t(keys.orders.totalLabel)}
              </span>
              <span className="text-[17px] font-extrabold tracking-[-0.01em] text-[color:var(--text-primary)]">
                {formatEgp(order.totalAmount)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* payment */}
      <div className="mt-[22px]">
        <div className={`${SECTION} mb-3`}>{t(keys.orders.paymentSectionTitle)}</div>
        <div className={`${CARD} px-[14px] py-[13px]`}>
          <div className="mb-[11px] flex items-center gap-2">
            <PayMethodGlyph method={order.paymentMethod} />
            <span className="text-[13.5px] font-bold text-[color:var(--text-primary)]">
              {methodLabel}
            </span>
          </div>
          <div className="rounded-[11px] px-3" style={{ background: insetBg }}>
            {payRows.map((r) => (
              <div key={r.label} className="flex items-center justify-between gap-3 py-[9px]">
                <span className="text-[12.5px] font-semibold text-[color:var(--text-secondary)]">
                  {r.label}
                </span>
                <span
                  className="text-[14px] font-extrabold tracking-[-0.01em]"
                  style={{ color: r.pos ? "var(--success-500)" : "var(--warning-500)" }}
                >
                  {r.amount}
                </span>
              </div>
            ))}
          </div>
          {order.paymentProofs.length > 0 ? (
            <div className="mt-[11px] flex flex-col gap-2">
              {order.paymentProofs.map((pf) => {
                // Proofs live in the private bucket; the signed URL arrives with
                // the proof query, so a row is inert until its URL lands.
                const url = proofUrlsById.get(pf.id) ?? null;
                const body = (
                  <>
                    <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-[color:var(--soft2)] text-[#3B82F6]">
                      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[12.5px] font-bold text-[color:var(--text-primary)]">
                        {formatEgp(pf.paidAmount)}
                      </span>
                      <span className="block text-[11px] text-[color:var(--text-muted)]">
                        {t(keys.orders.transferReceipt)} · {relativeAgo(pf.uploadedAt, locale)}
                      </span>
                    </span>
                    {url ? (
                      <span className="flex shrink-0 items-center gap-0.5 text-[12px] font-bold text-[#3B82F6]">
                        {t(keys.orders.viewLabel)}
                        <ChevronIcon />
                      </span>
                    ) : null}
                  </>
                );
                const row =
                  "flex items-center gap-[11px] rounded-[11px] border border-[color:var(--card-br)] p-[9px]";
                if (!url) {
                  return (
                    <span key={pf.id} className={row}>
                      {body}
                    </span>
                  );
                }
                return (
                  <a
                    key={pf.id}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={row}
                  >
                    {body}
                  </a>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      {/* customer & delivery */}
      <div className="mt-[22px]">
        <div className={`${SECTION} mb-3`}>{t(keys.orders.customerDeliveryTitle)}</div>
        <div className={`${CARD} p-[14px]`}>
          <div className="flex items-center gap-3">
            <Avatar name={order.customer.name} size={44} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14.5px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
                {order.customer.name || "—"}
              </div>
              <div className="mt-0.5 truncate text-[12.5px] text-[color:var(--text-muted)]">
                {order.customer.phoneE164}
              </div>
            </div>
            <a
              href={`tel:${order.customer.phoneE164}`}
              aria-label={t(keys.orders.callCustomer)}
              className="flex h-[38px] w-[38px] items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--card)] text-[color:var(--text-secondary)]"
            >
              <PhoneIcon />
            </a>
          </div>
          <div className="my-[13px] h-px bg-[color:var(--hair)]" />
          <div className="flex gap-2.5">
            <span className="shrink-0 pt-0.5 text-[color:var(--text-muted)]">
              <PinIcon />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-semibold tracking-[-0.01em] text-[color:var(--text-primary)]">
                {order.buyerAddress || "—"}
              </div>
              {govName ? (
                <div className="mt-px text-[12.5px] text-[color:var(--text-muted)]">{govName}</div>
              ) : null}
              {addressRows.length ? (
                <div className="mt-2 flex flex-col gap-1">
                  {addressRows.map((row) => (
                    <div key={row.label} className="flex items-baseline gap-2 text-[12px]">
                      <span className="shrink-0 text-[color:var(--text-muted)]">{row.label}</span>
                      <span className="min-w-0 flex-1 text-[color:var(--text-primary)]">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            {order.buyerAddress ? (
              <button
                type="button"
                onClick={() => void copyAddress()}
                aria-label={t(keys.orders.copyAddressAction)}
                title={t(keys.orders.copyAddressAction)}
                className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] border border-[color:var(--card-br)] bg-[color:var(--card)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
              >
                <CopyIcon />
              </button>
            ) : null}
          </div>
          {order.shipment?.trackingNumber ? (
            <>
              <div className="my-[13px] h-px bg-[color:var(--hair)]" />
              <div className="flex gap-2.5">
                <span className="shrink-0 pt-0.5 text-[color:var(--text-muted)]">
                  <TruckIcon />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-semibold tracking-[-0.01em] text-[color:var(--text-primary)]">
                    {t(keys.orders.trackingLabel)} · {order.shipment.trackingNumber}
                  </div>
                  {order.shipment.trackingUrl ? (
                    <a
                      href={order.shipment.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-px inline-block text-[12.5px] font-bold text-[color:var(--accent-500)]"
                    >
                      {t(keys.orders.drawerOpenTracking)}
                    </a>
                  ) : null}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* buyer note */}
      {order.buyerNote ? (
        <div className="mt-[22px]">
          <div className={`${SECTION} mb-3`}>{t(keys.orders.buyerNoteLabel)}</div>
          <div className={`${CARD} p-[14px]`}>
            <div className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-[color:var(--text-primary)]">
              {order.buyerNote}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── timeline builder ──
type TLState = "done" | "current";
function buildTimeline(
  order: SellerOrderDetail,
  locale: string,
  t: ReturnType<typeof useTranslations>,
): { label: string; sub: string; state: TLState }[] {
  const out: { label: string; sub: string; state: TLState }[] = [
    { label: t(keys.orders.timelinePlaced), sub: relativeAgo(order.createdAt, locale), state: "done" },
  ];
  const sorted = [...order.statusHistory].sort((a, b) => a.changedAt.localeCompare(b.changedAt));
  let last = out[0].label;
  for (const h of sorted) {
    const label = t(STATUS_GROUP_LABEL[statusGroup(h.newStatus as OrderStatus)]);
    if (label !== last) {
      out.push({ label, sub: relativeAgo(h.changedAt, locale), state: "done" });
      last = label;
    }
  }
  if (out.length > 0) out[out.length - 1].state = "current";
  return out;
}

// ── small pieces ──
function Chip({
  children,
  tint,
}: {
  children: React.ReactNode;
  tint?: { bg: string; fg: string };
}) {
  return (
    <span
      className="inline-flex items-center gap-[7px] rounded-full border py-[7px] pe-3 ps-[9px] text-[13px] font-semibold tracking-[-0.01em] [&_svg]:h-[15px] [&_svg]:w-[15px]"
      style={
        tint
          ? { background: tint.bg, color: tint.fg, borderColor: "transparent" }
          : {
              background: "var(--soft)",
              color: "var(--text-secondary)",
              borderColor: "var(--card-br)",
            }
      }
    >
      {children}
    </span>
  );
}

function Avatar({ name, size = 17 }: { name: string; size?: number }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full font-extrabold"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.46,
        background: "color-mix(in srgb, var(--accent-500) 14%, transparent)",
        color: "var(--accent-500)",
      }}
    >
      {initial}
    </span>
  );
}

function TimelineNode({ state }: { state: TLState }) {
  if (state === "current") {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" className="block">
        <circle cx="8" cy="8" r="7" fill="none" stroke="var(--accent-500)" strokeWidth="2" />
        <circle cx="8" cy="8" r="3" fill="var(--accent-500)" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" className="block">
      <circle cx="8" cy="8" r="7" fill="var(--accent-500)" />
      <path
        d="M5 8.2l2 2 4-4.4"
        fill="none"
        stroke="var(--on-accent)"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Payment-method glyph — a small tinted icon box before the method name. */
export function PayMethodGlyph({ method }: { method: string }) {
  const cod = method === "COD";
  const color = cod ? "var(--warning-500)" : "#8B5CF6";
  return (
    <span
      className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[7px]"
      style={{ background: `color-mix(in srgb, ${color} 16%, transparent)`, color }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {cod ? (
          <>
            <rect x="2" y="6" width="20" height="12" rx="2" />
            <circle cx="12" cy="12" r="2.6" />
          </>
        ) : (
          <>
            <rect x="2" y="5" width="20" height="14" rx="2" />
            <path d="M2 10h20" />
          </>
        )}
      </svg>
    </span>
  );
}

const ICON = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};
function BoxIcon() {
  return (
    <svg {...ICON}>
      <path d="M12 2.5 3.5 7v10L12 21.5 20.5 17V7z" />
      <path d="M3.5 7 12 11.5 20.5 7" />
      <path d="M12 11.5v10" />
    </svg>
  );
}
function PhoneIcon() {
  return (
    <svg {...ICON} width={16} height={16} strokeWidth={1.9}>
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.7a2 2 0 0 1-.5 2.1L8 9.6a16 16 0 0 0 6 6l1.1-1.1a2 2 0 0 1 2.1-.5c.9.3 1.8.5 2.7.6a2 2 0 0 1 1.7 2Z" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg {...ICON} width={16} height={16}>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="2.6" />
    </svg>
  );
}
function CopyIcon() {
  return (
    <svg {...ICON} width={16} height={16}>
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
function TruckIcon() {
  return (
    <svg {...ICON} width={16} height={16}>
      <path d="M5 7h11l3 4v6H5z" />
      <path d="M5 11h14" />
      <circle cx="8.5" cy="17.5" r="1.6" />
      <circle cx="16.5" cy="17.5" r="1.6" />
    </svg>
  );
}
function ChevronIcon() {
  return (
    <svg {...ICON} width={13} height={13} strokeWidth={2.2}>
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}
function AlertIcon() {
  return (
    <svg {...ICON} width={15} height={15} strokeWidth={1.9}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5M12 16h.01" />
    </svg>
  );
}
